import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnChanges, SimpleChanges, inject, input, model, output } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { CheckboxModule } from 'primeng/checkbox';

import { ComissaoEstruturaFiltroResponse, ComissaoLocalReuniaoResponse, ComissaoTipoComissaoResponse } from '@/core/api/sicom-api/comissoes/reunioes/comissao-reuniao.model';
import { SeletorArvoreComissaoComponent } from '@/features/comissoes/dashboard-comissoes/components/seletor-arvore-comissao/seletor-arvore-comissao';
import { ReuniaoComissao, ReuniaoSalvarPayload } from '@/features/comissoes/dashboard-comissoes/comissoes-dashboard.model';
import { CardContent } from '@/shared/components/card-content/card-content';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
interface OpcaoFormularioReuniao {
    label: string;
    value: number | string;
    idTipoComissaoCt?: number;
}

@Component({
    selector: 'app-formulario-reuniao-comissoes',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DialogoPadraoDirective, CommonModule, ReactiveFormsModule, ButtonModule, DialogModule, InputTextModule, SelectModule, SeletorArvoreComissaoComponent, CheckboxModule, CardContent, CanDirective],
    templateUrl: './formulario-reuniao-comissoes.html',
    styleUrls: ['./formulario-reuniao-comissoes.scss']
})
/** Modal de agendamento/edição de reunião de comissão (form tipado + seletor de estrutura em árvore). */
export class FormularioReuniaoComissoesComponent implements OnChanges {
    private readonly formBuilder = inject(FormBuilder);

    /** Visibilidade do modal (two-way `[(visivel)]`). */
    readonly visivel = model(false);
    /** Reunião em edição (null = agendar nova). */
    readonly reuniao = input<ReuniaoComissao | null>(null);
    /** Locais de reunião disponíveis. */
    readonly locais = input<ComissaoLocalReuniaoResponse[]>([]);
    /** Tipos de comissão disponíveis. */
    readonly tiposComissao = input<ComissaoTipoComissaoResponse[]>([]);
    /** Estruturas (comissões/CT/GT) para o seletor em árvore. */
    readonly estruturasComissao = input<ComissaoEstruturaFiltroResponse[]>([]);
    /** Indica carregamento das estruturas. */
    readonly carregandoEstruturas = input(false);
    /** Emite o payload da reunião ao salvar. */
    readonly salvar = output<ReuniaoSalvarPayload>();
    /** Pede o carregamento das estruturas (ao abrir o modal). */
    readonly carregarEstruturas = output<void>();

    readonly modalidades = [
        { label: 'Hibrida', value: 'H' },
        { label: 'Presencial', value: 'P' },
        { label: 'Virtual', value: 'R' }
    ];

    readonly formulario = this.formBuilder.nonNullable.group({
        dataReuniao: ['', Validators.required],
        horaInicio: ['08:00', Validators.required],
        horaFim: ['18:00', Validators.required],
        idLocalReuniao: [0, [Validators.required, Validators.min(1)]],
        idTipoComissaoCt: [0, [Validators.required, Validators.min(1)]],
        idEstruturaOrganizacional: [0, [Validators.required, Validators.min(1)]],
        inModalidade: ['H', Validators.required],
        inVisivelPortal: [true]
    });

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['visivel'] && this.visivel()) {
            if (!this.estruturasComissao().length) {
                this.carregarEstruturas.emit();
            }
            this.preencherFormulario();
        }

        if (this.visivel() && !this.reuniao() && (changes['locais'] || changes['tiposComissao'])) {
            this.aplicarPadroesCadastro();
        }
    }

    get titulo(): string {
        const reuniao = this.reuniao();
        return reuniao ? `Editar reunião ${reuniao.codigo}` : 'Agendar nova reunião';
    }

    /** Permissão do botão Salvar: editar quando há reunião carregada, senão criar. */
    protected get roleConfirmar(): string {
        return this.reuniao() ? PERMISSOES.comissoes.reunioes.editar : PERMISSOES.comissoes.reunioes.criar;
    }

    get opcoesLocais(): OpcaoFormularioReuniao[] {
        return this.locais()
            .map((local) => ({
                label: (local.dsLocalReuniao ?? '').trim() || 'Nenhum',
                value: Number(local.idLocalReuniao ?? 0)
            }))
            .filter((local) => Number(local.value) > 0);
    }

    get opcoesTipos(): OpcaoFormularioReuniao[] {
        return this.tiposComissao()
            .map((tipo) => ({
                label: (tipo.dsTipoComissaoCt ?? '').trim() || `Tipo ${tipo.idTipoComissaoCt}`,
                value: Number(tipo.idTipoComissaoCt ?? 0)
            }))
            .filter((tipo) => Number(tipo.value) > 0);
    }

    get opcoesComissoes(): OpcaoFormularioReuniao[] {
        const idTipo = Number(this.formulario.controls.idTipoComissaoCt.value);
        return this.estruturasComissao()
            .filter((estrutura) => !idTipo || Number(estrutura.idTipoComissaoCt ?? 0) === idTipo)
            .map((estrutura) => ({
                label: `${estrutura.idEstruturaOrganizacional} - ${(estrutura.dsEstruturaOrganizacional ?? '').trim()}`,
                value: Number(estrutura.idEstruturaOrganizacional ?? 0),
                idTipoComissaoCt: estrutura.idTipoComissaoCt
            }))
            .filter((estrutura) => Number(estrutura.value) > 0)
            .sort((a, b) => a.label.localeCompare(b.label));
    }

    aoAlterarTipo(): void {
        this.formulario.patchValue({ idEstruturaOrganizacional: 0 });
    }

    fechar(): void {
        this.visivel.set(false);
    }

    confirmar(): void {
        if (this.formulario.invalid) {
            this.formulario.markAllAsTouched();
            return;
        }

        const valor = this.formulario.getRawValue();
        const dhInicioReuniao = this.montarDataHora(valor.dataReuniao, valor.horaInicio);
        const dhFimReuniao = this.montarDataHora(valor.dataReuniao, valor.horaFim);
        if (!dhInicioReuniao || !dhFimReuniao || dhFimReuniao < dhInicioReuniao) {
            this.formulario.controls.horaFim.setErrors({ periodoInvalido: true });
            return;
        }

        this.salvar.emit({
            dhInicioReuniao,
            dhFimReuniao,
            idLocalReuniao: Number(valor.idLocalReuniao),
            idEstruturaOrganizacional: Number(valor.idEstruturaOrganizacional),
            idTipoComissaoCt: Number(valor.idTipoComissaoCt),
            idSolicitanteAssistente: this.reuniao()?.idSolicitanteAssistente ?? null,
            ufSolicitanteAssistente: this.reuniao()?.ufSolicitanteAssistente ?? null,
            idSolicitanteCoordenador: this.reuniao()?.idSolicitanteCoordenador ?? null,
            sgUfCoordenador: this.reuniao()?.ufSolicitanteCoordenador ?? null,
            inModalidade: valor.inModalidade,
            inVisivelPortal: valor.inVisivelPortal ? 'S' : 'N'
        });
    }

    private preencherFormulario(): void {
        const inicio = this.reuniao()?.dataInicio;
        const fim = this.reuniao()?.dataTermino;
        this.formulario.reset({
            dataReuniao: this.formatarDataInput(inicio ?? new Date()),
            horaInicio: this.formatarHoraInput(inicio) || '08:00',
            horaFim: this.formatarHoraInput(fim) || '18:00',
            idLocalReuniao: this.reuniao()?.idLocalReuniao ?? this.primeiroLocal(),
            idTipoComissaoCt: this.reuniao()?.idTipoComissaoCt ?? this.primeiroTipo(),
            idEstruturaOrganizacional: this.reuniao()?.idEstruturaOrganizacional ?? 0,
            inModalidade: this.mapearModalidadeValor(this.reuniao()?.modalidade),
            inVisivelPortal: this.reuniao()?.visivelPortal ?? true
        });

        if (!this.reuniao()) {
            this.aplicarPadroesCadastro();
        }
    }

    private aplicarPadroesCadastro(): void {
        const atual = this.formulario.getRawValue();
        const primeiroTipo = atual.idTipoComissaoCt || this.primeiroTipo();
        this.formulario.patchValue({
            idLocalReuniao: atual.idLocalReuniao || this.primeiroLocal(),
            idTipoComissaoCt: primeiroTipo
        });
    }

    private primeiroLocal(): number {
        return Number(this.locais().find((local) => local.idLocalReuniao !== undefined)?.idLocalReuniao ?? 0);
    }

    private primeiroTipo(): number {
        return Number(this.tiposComissao().find((tipo) => tipo.idTipoComissaoCt)?.idTipoComissaoCt ?? 0);
    }

    private montarDataHora(data: string, hora: string): string {
        if (!data || !hora) {
            return '';
        }
        return `${data}T${hora.length === 5 ? `${hora}:00` : hora}`;
    }

    private formatarDataInput(data: Date): string {
        return `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}-${String(data.getDate()).padStart(2, '0')}`;
    }

    private formatarHoraInput(data?: Date | null): string {
        if (!data) {
            return '';
        }
        return `${String(data.getHours()).padStart(2, '0')}:${String(data.getMinutes()).padStart(2, '0')}`;
    }

    private mapearModalidadeValor(modalidade?: string): string {
        const valor = (modalidade ?? '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toUpperCase();
        if (valor.includes('PRESENC')) {
            return 'P';
        }
        if (valor.includes('VIRTUAL')) {
            return 'R';
        }
        return 'H';
    }
}
